/**
 * SuppliersPage — iter150AZ.130
 *
 * Modeled after the Inventory and Customers pages. Two columns:
 *   • Left  — paginated search + list (table, click row to load detail)
 *   • Right — detail tabs: Overview, Contacts, Purchase Orders, Notes
 *
 * Access:
 *   • Visible only to users with `can_view_suppliers` (or super-admin).
 *   • Mutations (create / edit / contacts CRUD / add note) require
 *     `can_edit_suppliers` (or super-admin).
 *
 * All labels are translated via the `tip()` pattern (EN / FR / ES).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Factory, MagnifyingGlass, Plus, Pencil, Trash, Phone, Globe,
  EnvelopeSimple, Receipt, NotePencil, CheckCircle, XCircle,
  ArrowLeft, FloppyDisk, X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import HeaderBrand from "@/components/HeaderBrand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import {
  fetchSuppliers, fetchSupplier, fetchSupplierContacts,
  fetchSupplierPurchaseOrders, fetchSupplierNotes,
  createSupplier, updateSupplier,
  addSupplierContact, updateSupplierContact, deleteSupplierContact,
  addSupplierNote,
  fetchSupplierVendorShipments,
} from "@/lib/api";
import { VendorShipmentDetailModal } from "@/pages/VendorShipmentsPage";


// ─── i18n helper (matches the rest of the app) ────────────────────
function makeTip(lang) {
  return (en, fr, es) => {
    if (lang === "fr") return fr || en;
    if (lang === "es") return es || en;
    return en;
  };
}


const STATUS_OPTIONS = ["", "Active", "Inactive", "On Hold"];
const TYPE_OPTIONS   = ["", "Manufacturer", "Distributor", "Service",
                         "Logistics", "Spare Parts", "Other"];


export default function SuppliersPage({ user, onLogout, onLogoutAll }) {
  const { lang } = useI18n();
  const tip = useMemo(() => makeTip(lang), [lang]);
  const navigate = useNavigate();

  // Permissions ─ super-admin OR can_edit_suppliers can mutate.
  const canEdit = !!(user?.is_super_admin
                       || user?.permissions?.can_edit_suppliers
                       || user?.user_rights?.permissions?.can_edit_suppliers);

  // Search / filter state
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);

  // List state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Selected supplier + tab
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("overview");

  // Detail panel state
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [pos, setPos] = useState({ items: [], rollup: null });
  const [notes, setNotes] = useState([]);
  const [invoices, setInvoices] = useState({ items: [], rollup: { count: 0, total_spend: 0 } });
  const [invoiceModalId, setInvoiceModalId] = useState(null);

  // Add/edit supplier dialog
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  // Add/edit contact dialog
  const [contactDialog, setContactDialog] = useState(null);  // {mode:'add'|'edit', contact:{}}
  // Add note dialog
  const [showNote, setShowNote] = useState(false);

  // ─── Load list ────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSuppliers({
        q: q.trim(),
        type: typeF || undefined,
        status: statusF || undefined,
        page,
        per_page: perPage,
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [q, typeF, statusF, page, perPage]);

  useEffect(() => { load(); }, [load]);

  // ─── Load detail when selectedId changes ──────────────────────
  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setDetailBusy(true);
    try {
      const [det, ct, po, nt, inv] = await Promise.all([
        fetchSupplier(id),
        fetchSupplierContacts(id),
        fetchSupplierPurchaseOrders(id, 100),
        fetchSupplierNotes(id, 50),
        fetchSupplierVendorShipments(id, 200).catch(() => ({ items: [], rollup: { count: 0, total_spend: 0 } })),
      ]);
      setDetail(det);
      setContacts(ct.items || []);
      setPos({ items: po.items || [], rollup: po.rollup || null });
      setNotes(nt.items || []);
      setInvoices({
        items: inv?.items || [],
        rollup: inv?.rollup || { count: 0, total_spend: 0 },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setDetailBusy(false);
    }
  }, []);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="min-h-screen bg-slate-50">
      <HeaderBrand user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />

      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")}
                   data-testid="suppliers-back-btn">
            <ArrowLeft size={14} className="mr-1" />
            {tip("Back", "Retour", "Atrás")}
          </Button>
          <Factory size={22} weight="bold" className="text-indigo-700" />
          <h1 className="text-xl font-bold text-slate-900">
            {tip("Suppliers", "Fournisseurs", "Proveedores")}
          </h1>
          <span className="text-xs text-slate-400 ml-2">
            {total} {tip("supplier", "fournisseur", "proveedor")}{total === 1 ? "" : "s"}
          </span>
          {canEdit && (
            <Button size="sm" className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white"
                     onClick={() => setShowCreate(true)}
                     data-testid="supplier-create-btn">
              <Plus size={14} className="mr-1" weight="bold" />
              {tip("New supplier", "Nouveau fournisseur", "Nuevo proveedor")}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* ─── Left: search + table ────────────────────────── */}
          <div className="col-span-12 lg:col-span-7 bg-white border border-slate-200 rounded-md p-3">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="flex-1 min-w-[200px] relative">
                <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="supplier-search-input"
                  placeholder={tip("Search by company, ID, phone, email…",
                                    "Rechercher par société, ID, téléphone, e-mail…",
                                    "Buscar por empresa, ID, teléfono, correo…")}
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setPage(1); }}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  className="pl-7 h-9 text-sm"
                />
              </div>
              <select data-testid="supplier-filter-type"
                       value={typeF} onChange={(e) => { setTypeF(e.target.value); setPage(1); }}
                       className="h-9 text-xs border border-slate-300 rounded px-2 bg-white">
                {TYPE_OPTIONS.map(t => (
                  <option key={t} value={t}>{t || tip("All types", "Tous les types", "Todos los tipos")}</option>
                ))}
              </select>
              <select data-testid="supplier-filter-status"
                       value={statusF} onChange={(e) => { setStatusF(e.target.value); setPage(1); }}
                       className="h-9 text-xs border border-slate-300 rounded px-2 bg-white">
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s || tip("Any status", "Tout statut", "Cualquier estado")}</option>
                ))}
              </select>
              <Button size="sm" onClick={load} disabled={loading}
                       data-testid="supplier-search-btn">
                {loading ? tip("Loading…", "Chargement…", "Cargando…")
                         : tip("Search", "Rechercher", "Buscar")}
              </Button>
            </div>

            <div className="overflow-x-auto border border-slate-100 rounded">
              <table className="w-full text-xs" data-testid="suppliers-table">
                <thead className="bg-slate-100 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="px-2 py-2 font-semibold">{tip("Company", "Société", "Empresa")}</th>
                    <th className="px-2 py-2 font-semibold">{tip("Type", "Type", "Tipo")}</th>
                    <th className="px-2 py-2 font-semibold">{tip("Phone", "Téléphone", "Teléfono")}</th>
                    <th className="px-2 py-2 font-semibold text-right">{tip("POs", "Bons", "Órdenes")}</th>
                    <th className="px-2 py-2 font-semibold text-right">{tip("Total spend", "Dépense totale", "Gasto total")}</th>
                    <th className="px-2 py-2 font-semibold">{tip("Status", "Statut", "Estado")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && !loading && (
                    <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400">
                      {tip("No suppliers match.", "Aucun fournisseur correspondant.",
                            "Sin coincidencias.")}
                    </td></tr>
                  )}
                  {rows.map((s) => {
                    const active = selectedId === s.supplier_id;
                    return (
                      <tr key={s.supplier_id}
                          data-testid="supplier-row"
                          onClick={() => setSelectedId(s.supplier_id)}
                          className={"border-t border-slate-100 cursor-pointer transition-colors "
                                      + (active ? "bg-indigo-50" : "hover:bg-slate-50")}>
                        <td className="px-2 py-2 font-medium text-slate-900">
                          {s.company_name || "—"}
                        </td>
                        <td className="px-2 py-2 text-slate-600">{s.type || "—"}</td>
                        <td className="px-2 py-2 text-slate-600">{s.company_tel || "—"}</td>
                        <td className="px-2 py-2 text-right font-mono">{s.po_count}</td>
                        <td className="px-2 py-2 text-right font-mono">
                          ${Number(s.total_spend || 0).toLocaleString(undefined,
                                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-2 py-2">
                          <StatusBadge status={s.company_status} verified={s.verification_status} tip={tip} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-2 text-xs">
                <div className="text-slate-500">
                  {tip("Page", "Page", "Página")} {page} {tip("of", "sur", "de")} {totalPages}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" disabled={page === 1}
                           onClick={() => setPage(p => Math.max(1, p - 1))}
                           data-testid="supplier-prev-page">
                    {tip("Prev", "Préc", "Ant")}
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= totalPages}
                           onClick={() => setPage(p => p + 1)}
                           data-testid="supplier-next-page">
                    {tip("Next", "Suiv", "Sig")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ─── Right: detail panel ──────────────────────────── */}
          <div className="col-span-12 lg:col-span-5 bg-white border border-slate-200 rounded-md p-3"
               data-testid="supplier-detail-panel">
            {!selectedId && (
              <div className="text-sm text-slate-400 text-center py-8">
                {tip("Select a supplier on the left to see details.",
                      "Sélectionnez un fournisseur à gauche pour voir les détails.",
                      "Seleccione un proveedor a la izquierda para ver detalles.")}
              </div>
            )}
            {selectedId && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-base font-bold text-slate-900 flex-1">
                    {detail?.company_name || tip("Loading…", "Chargement…", "Cargando…")}
                  </h3>
                  {canEdit && detail && (
                    <Button size="sm" variant="outline" onClick={() => setShowEdit(true)}
                             data-testid="supplier-edit-btn">
                      <Pencil size={12} className="mr-1" />
                      {tip("Edit", "Modifier", "Editar")}
                    </Button>
                  )}
                </div>
                <div className="flex gap-1 border-b border-slate-200 mb-3 text-xs">
                  {[
                    { k: "overview", label: tip("Overview", "Aperçu", "Resumen") },
                    { k: "contacts", label: tip("Contacts", "Contacts", "Contactos"), count: contacts.length },
                    { k: "pos",      label: tip("Purchase orders", "Bons de commande", "Órdenes de compra"), count: pos.items.length },
                    { k: "invoices", label: tip("Invoices", "Factures", "Facturas"), count: invoices.items.length },
                    { k: "notes",    label: tip("Notes", "Notes", "Notas"), count: notes.length },
                  ].map(t => (
                    <button key={t.k}
                            data-testid={`supplier-tab-${t.k}`}
                            onClick={() => setTab(t.k)}
                            className={"px-3 py-2 font-semibold transition-colors " + (
                              tab === t.k
                                ? "border-b-2 border-indigo-600 text-indigo-700"
                                : "text-slate-500 hover:text-slate-700"
                            )}>
                      {t.label}{typeof t.count === "number" && (
                        <span className="ml-1 text-slate-400 font-normal">({t.count})</span>
                      )}
                    </button>
                  ))}
                </div>

                {detailBusy && (
                  <div className="text-xs text-slate-400 text-center py-4">
                    {tip("Loading…", "Chargement…", "Cargando…")}
                  </div>
                )}

                {!detailBusy && tab === "overview" && detail && (
                  <OverviewPanel detail={detail} tip={tip} />
                )}
                {!detailBusy && tab === "contacts" && (
                  <ContactsPanel
                    contacts={contacts}
                    canEdit={canEdit}
                    tip={tip}
                    onAdd={() => setContactDialog({ mode: "add", contact: {} })}
                    onEdit={(c) => setContactDialog({ mode: "edit", contact: c })}
                    onDelete={async (c) => {
                      if (!window.confirm(tip(
                        `Delete contact "${c.name}"?`,
                        `Supprimer le contact "${c.name}" ?`,
                        `¿Eliminar el contacto "${c.name}"?`))) return;
                      try {
                        await deleteSupplierContact(selectedId, c.contact_id);
                        toast.success(tip("Contact deleted", "Contact supprimé", "Contacto eliminado"));
                        loadDetail(selectedId);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e.message);
                      }
                    }} />
                )}
                {!detailBusy && tab === "pos" && (
                  <PurchaseOrdersPanel pos={pos} tip={tip} />
                )}
                {!detailBusy && tab === "invoices" && (
                  <SupplierInvoicesPanel
                    invoices={invoices}
                    tip={tip}
                    onOpenInvoice={(id) => setInvoiceModalId(id)} />
                )}
                {!detailBusy && tab === "notes" && (
                  <NotesPanel
                    notes={notes}
                    canEdit={canEdit}
                    tip={tip}
                    onAdd={() => setShowNote(true)} />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── Dialogs ─────────────────────────────────────────── */}
      {showCreate && (
        <SupplierFormDialog
          mode="create"
          tip={tip}
          onClose={() => setShowCreate(false)}
          onSaved={(id) => {
            setShowCreate(false);
            load();
            if (id) setSelectedId(id);
          }}
        />
      )}
      {showEdit && detail && (
        <SupplierFormDialog
          mode="edit"
          tip={tip}
          initial={detail}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            loadDetail(selectedId);
            load();
          }}
        />
      )}
      {contactDialog && (
        <ContactFormDialog
          mode={contactDialog.mode}
          initial={contactDialog.contact}
          tip={tip}
          onClose={() => setContactDialog(null)}
          onSaved={async (payload) => {
            try {
              if (contactDialog.mode === "add") {
                await addSupplierContact(selectedId, payload);
                toast.success(tip("Contact added", "Contact ajouté", "Contacto añadido"));
              } else {
                await updateSupplierContact(selectedId, contactDialog.contact.contact_id, payload);
                toast.success(tip("Contact updated", "Contact mis à jour", "Contacto actualizado"));
              }
              setContactDialog(null);
              loadDetail(selectedId);
            } catch (e) {
              toast.error(e?.response?.data?.detail || e.message);
            }
          }}
        />
      )}
      {showNote && (
        <NoteFormDialog
          tip={tip}
          onClose={() => setShowNote(false)}
          onSaved={async (payload) => {
            try {
              await addSupplierNote(selectedId, payload);
              toast.success(tip("Note added", "Note ajoutée", "Nota añadida"));
              setShowNote(false);
              loadDetail(selectedId);
            } catch (e) {
              toast.error(e?.response?.data?.detail || e.message);
            }
          }}
        />
      )}
      {invoiceModalId && (
        <VendorShipmentDetailModal
          shipmentId={invoiceModalId}
          onClose={() => setInvoiceModalId(null)}
        />
      )}
    </div>
  );
}


// ─── Sub-components ─────────────────────────────────────────────
function StatusBadge({ status, verified, tip }) {
  const cls = (() => {
    if ((status || "").toLowerCase() === "inactive") return "bg-slate-100 text-slate-500";
    if ((status || "").toLowerCase().startsWith("on hold")) return "bg-amber-50 text-amber-700";
    return "bg-emerald-50 text-emerald-700";
  })();
  return (
    <div className="inline-flex items-center gap-1">
      <span className={"inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold " + cls}>
        {status || tip("Active", "Actif", "Activo")}
      </span>
      {(verified || "").toUpperCase() === "Y" && (
        <CheckCircle size={11} weight="fill" className="text-emerald-600"
                       title={tip("Verified", "Vérifié", "Verificado")} />
      )}
    </div>
  );
}

function OverviewRow({ label, value, mono }) {
  return (
    <div className="flex gap-2 text-xs py-1">
      <div className="w-36 text-slate-500">{label}</div>
      <div className={"text-slate-800 break-all " + (mono ? "font-mono" : "")}>{value || "—"}</div>
    </div>
  );
}

function OverviewPanel({ detail, tip }) {
  const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined,
                              { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-3" data-testid="supplier-overview">
      <div className="grid grid-cols-3 gap-2">
        <KpiCard label={tip("Total POs", "Total bons", "Órdenes totales")}
                  value={String(detail.po_count || 0)} icon={Receipt} />
        <KpiCard label={tip("Total spend", "Dépense totale", "Gasto total")}
                  value={fmt$(detail.total_spend)} icon={Receipt} />
        <KpiCard label={tip("Last PO", "Dernier bon", "Última orden")}
                  value={detail.last_po_date || "—"} icon={Receipt} />
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded p-2">
        <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">
          {tip("Contact", "Contact", "Contacto")}
        </div>
        <OverviewRow label={tip("Address", "Adresse", "Dirección")} value={detail.address} />
        <OverviewRow label={tip("Phone", "Téléphone", "Teléfono")} value={detail.company_tel} />
        <OverviewRow label={tip("Website", "Site web", "Sitio web")} value={detail.company_web_site} />
        <OverviewRow label={tip("Email", "E-mail", "Correo")} value={detail.company_admin_email} />
        <OverviewRow label={tip("Contact method", "Méthode de contact", "Método de contacto")} value={detail.contact_method} />
        <OverviewRow label={tip("Last contact", "Dernier contact", "Último contacto")} value={detail.contact_date} />
        <OverviewRow label={tip("Responsible (US Air)", "Responsable (US Air)", "Responsable (US Air)")}
              value={detail.usair_responsible} />
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded p-2">
        <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">
          {tip("Banking", "Bancaire", "Bancario")}
        </div>
        <OverviewRow label={tip("Bank", "Banque", "Banco")} value={detail.bank_name} />
        <OverviewRow label={tip("Branch", "Agence", "Sucursal")} value={detail.branch} />
        <OverviewRow label={tip("Account #", "N° de compte", "N° de cuenta")} value={detail.account_number} mono />
        <OverviewRow label="IBAN" value={detail.iban} mono />
        <OverviewRow label={tip("Routing #", "Routing", "ABA")} value={detail.routing_number} mono />
        <OverviewRow label="SWIFT" value={detail.swift_code} mono />
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-slate-200 rounded-md px-2 py-2">
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-500 font-bold">
        {Icon && <Icon size={10} weight="bold" />}
        {label}
      </div>
      <div className="text-sm font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function ContactsPanel({ contacts, canEdit, tip, onAdd, onEdit, onDelete }) {
  return (
    <div data-testid="supplier-contacts-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-slate-500">
          {contacts.length} {tip("contact", "contact", "contacto")}{contacts.length === 1 ? "" : "s"}
        </div>
        {canEdit && (
          <Button size="sm" onClick={onAdd}
                   data-testid="supplier-contact-add-btn"
                   className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 px-2 text-xs">
            <Plus size={11} className="mr-1" weight="bold" />
            {tip("Add contact", "Ajouter un contact", "Añadir contacto")}
          </Button>
        )}
      </div>
      {contacts.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-4">
          {tip("No contacts yet.", "Aucun contact pour l'instant.", "Sin contactos aún.")}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {contacts.map((c) => (
            <li key={c.contact_id}
                data-testid="supplier-contact-row"
                className="border border-slate-200 rounded p-2 text-xs">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  {c.type && <div className="text-[10px] text-slate-500 uppercase tracking-wider">{c.type}</div>}
                  <div className="mt-1 space-y-0.5 text-slate-600">
                    {c.email && (
                      <div className="flex items-center gap-1 break-all">
                        <EnvelopeSimple size={11} /> {c.email}
                      </div>
                    )}
                    {c.phone && (
                      <div className="flex items-center gap-1"><Phone size={11} /> {c.phone}</div>
                    )}
                    {c.explanation && (
                      <div className="text-[10px] text-slate-400 mt-1 italic">{c.explanation}</div>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                             data-testid="supplier-contact-edit-btn"
                             onClick={() => onEdit(c)}>
                      <Pencil size={11} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                             data-testid="supplier-contact-delete-btn"
                             onClick={() => onDelete(c)}>
                      <Trash size={11} />
                    </Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PurchaseOrdersPanel({ pos, tip }) {
  const navigate = useNavigate();
  const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined,
                              { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div data-testid="supplier-pos-panel">
      {pos.rollup && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          <KpiCard label={tip("PO count", "Nb de bons", "N° órdenes")}
                    value={String(pos.rollup.po_count || 0)} icon={Receipt} />
          <KpiCard label={tip("Total spend", "Dépense totale", "Gasto total")}
                    value={fmt$(pos.rollup.total_spend)} icon={Receipt} />
          <KpiCard label={tip("Avg PO", "Bon moyen", "Promedio")}
                    value={fmt$(pos.rollup.avg_spend)} icon={Receipt} />
        </div>
      )}
      {pos.items.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-4">
          {tip("No purchase orders.", "Aucun bon de commande.", "Sin órdenes de compra.")}
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-100 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-slate-600">
                <th className="px-2 py-1.5 font-semibold">PO #</th>
                <th className="px-2 py-1.5 font-semibold">{tip("Date", "Date", "Fecha")}</th>
                <th className="px-2 py-1.5 font-semibold">{tip("Buyer", "Acheteur", "Comprador")}</th>
                <th className="px-2 py-1.5 font-semibold text-right">{tip("Lines", "Lignes", "Líneas")}</th>
                <th className="px-2 py-1.5 font-semibold text-right">{tip("Subtotal", "Sous-total", "Subtotal")}</th>
                <th className="px-2 py-1.5 font-semibold">{tip("Status", "Statut", "Estado")}</th>
              </tr>
            </thead>
            <tbody>
              {pos.items.map((p) => (
                <tr key={p.id}
                     data-testid={`supplier-po-row-${p.poid || p.id}`}
                     onClick={() => p.poid && navigate(`/admin/purchase-orders/${encodeURIComponent(p.poid)}`)}
                     className={"border-t border-slate-100 hover:bg-indigo-50/50 " + (p.poid ? "cursor-pointer" : "")}>
                  <td className="px-2 py-1.5 font-mono font-bold text-indigo-700">{p.poid || p.id}</td>
                  <td className="px-2 py-1.5">{p.po_date || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-600">{p.buyer_customer || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{p.line_count}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt$(p.subtotal)}</td>
                  <td className="px-2 py-1.5 text-slate-600">{p.last_action || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function SupplierInvoicesPanel({ invoices, tip, onOpenInvoice }) {
  const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined,
                              { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const items = invoices?.items || [];
  const roll = invoices?.rollup || { count: 0, total_spend: 0 };
  return (
    <div data-testid="supplier-invoices-panel">
      <div className="grid grid-cols-2 gap-2 mb-2">
        <KpiCard label={tip("Invoices", "Factures", "Facturas")}
                    value={String(roll.count || 0)} icon={Receipt} />
        <KpiCard label={tip("Total billed", "Total facturé", "Total facturado")}
                    value={fmt$(roll.total_spend)} icon={Receipt} />
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-4"
              data-testid="supplier-invoices-empty">
          {tip("No invoices on file.", "Aucune facture enregistrée.", "Sin facturas registradas.")}
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-100 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-slate-600">
                <th className="px-2 py-1.5 font-semibold">{tip("Invoice #", "Facture nº", "Factura nº")}</th>
                <th className="px-2 py-1.5 font-semibold">{tip("Date", "Date", "Fecha")}</th>
                <th className="px-2 py-1.5 font-semibold">PO</th>
                <th className="px-2 py-1.5 font-semibold text-right">{tip("Total", "Total", "Total")}</th>
                <th className="px-2 py-1.5 font-semibold">{tip("File", "Fichier", "Archivo")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}
                     data-testid={`supplier-invoice-row-${it.id}`}
                     onClick={() => onOpenInvoice && onOpenInvoice(it.id)}
                     className="border-t border-slate-100 hover:bg-indigo-50/50 cursor-pointer">
                  <td className="px-2 py-1.5 font-mono font-bold text-indigo-700">{it.invoice_number || `#${it.id}`}</td>
                  <td className="px-2 py-1.5">{it.invoice_date || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-slate-600">{it.po_link || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {it.total != null ? fmt$(it.total) : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-slate-500">{it.file_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NotesPanel({ notes, canEdit, tip, onAdd }) {
  return (
    <div data-testid="supplier-notes-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-slate-500">
          {notes.length} {tip("note", "note", "nota")}{notes.length === 1 ? "" : "s"}
        </div>
        {canEdit && (
          <Button size="sm" onClick={onAdd}
                   data-testid="supplier-note-add-btn"
                   className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 px-2 text-xs">
            <NotePencil size={11} className="mr-1" weight="bold" />
            {tip("Add note", "Ajouter une note", "Añadir nota")}
          </Button>
        )}
      </div>
      {notes.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-4">
          {tip("No notes yet.", "Aucune note pour l'instant.", "Sin notas aún.")}
        </div>
      ) : (
        <ul className="space-y-2">
          {notes.map(n => (
            <li key={n.note_id} className="border border-slate-200 rounded p-2 text-xs"
                data-testid="supplier-note-row">
              <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                <span>{n.note_dt}</span>
                <span className="font-mono">{n.owner}</span>
              </div>
              {n.note_for && (
                <div className="text-[10px] text-indigo-700 mb-1">
                  {tip("For:", "Pour :", "Para:")} <b>{n.note_for}</b>
                </div>
              )}
              <div className="text-slate-800 whitespace-pre-wrap">{n.note_body}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ─── Dialogs ───────────────────────────────────────────────────
function SupplierFormDialog({ mode, initial = {}, tip, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    company_name:        initial.company_name || "",
    type:                initial.type || "Manufacturer",
    address:             initial.address || "",
    company_tel:         initial.company_tel || "",
    company_web_site:    initial.company_web_site || "",
    company_admin_email: initial.company_admin_email || "",
    usair_responsible:   initial.usair_responsible || "",
    company_status:      initial.company_status || "Active",
    verification_status: initial.verification_status || "",
    bank_name:           initial.bank_name || "",
    branch:              initial.branch || "",
    account_number:      initial.account_number || "",
    iban:                initial.iban || "",
    routing_number:      initial.routing_number || "",
    swift_code:          initial.swift_code || "",
    primary_contact_name:  "",
    primary_contact_email: "",
    primary_contact_phone: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.company_name.trim()) {
      toast.error(tip("Company name is required",
                       "Le nom de la société est requis",
                       "Se requiere el nombre de la empresa"));
      return;
    }
    setBusy(true);
    try {
      if (mode === "create") {
        const body = {
          ...form,
          primary_contact: (form.primary_contact_name.trim()
            ? {
                name:  form.primary_contact_name.trim(),
                email: form.primary_contact_email.trim(),
                phone: form.primary_contact_phone.trim(),
                type:  "Primary",
              }
            : null),
        };
        delete body.primary_contact_name;
        delete body.primary_contact_email;
        delete body.primary_contact_phone;
        const r = await createSupplier(body);
        toast.success(tip("Supplier created", "Fournisseur créé", "Proveedor creado"));
        onSaved(r.supplier_id);
      } else {
        // Only send fields that differ from `initial`.
        const patch = {};
        for (const k of Object.keys(form)) {
          if (k.startsWith("primary_contact_")) continue;
          if (form[k] !== (initial[k] || "")) patch[k] = form[k];
        }
        if (Object.keys(patch).length === 0) {
          toast.info(tip("Nothing to save", "Rien à enregistrer", "Nada que guardar"));
          setBusy(false);
          return;
        }
        await updateSupplier(initial.supplier_id, patch);
        toast.success(tip("Saved", "Enregistré", "Guardado"));
        onSaved();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] flex flex-col"
                      data-testid="supplier-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? tip("New supplier", "Nouveau fournisseur", "Nuevo proveedor")
              : tip("Edit supplier", "Modifier le fournisseur", "Editar proveedor")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1 space-y-3 min-h-0">
          <Field label={tip("Company name *", "Nom de la société *", "Nombre empresa *")}>
            <Input data-testid="supplier-form-company-name"
                    value={form.company_name} onChange={e => set("company_name", e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label={tip("Type", "Type", "Tipo")}>
              <select value={form.type} onChange={e => set("type", e.target.value)}
                       className="h-9 text-xs border border-slate-300 rounded px-2 w-full bg-white"
                       data-testid="supplier-form-type">
                {TYPE_OPTIONS.filter(Boolean).map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label={tip("Status", "Statut", "Estado")}>
              <select value={form.company_status} onChange={e => set("company_status", e.target.value)}
                       className="h-9 text-xs border border-slate-300 rounded px-2 w-full bg-white"
                       data-testid="supplier-form-status">
                {STATUS_OPTIONS.filter(Boolean).map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label={tip("Address", "Adresse", "Dirección")}>
            <Textarea value={form.address} onChange={e => set("address", e.target.value)}
                       rows={2} data-testid="supplier-form-address" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label={tip("Phone", "Téléphone", "Teléfono")}>
              <Input value={form.company_tel} onChange={e => set("company_tel", e.target.value)}
                      data-testid="supplier-form-phone" />
            </Field>
            <Field label={tip("Website", "Site web", "Sitio web")}>
              <Input value={form.company_web_site} onChange={e => set("company_web_site", e.target.value)}
                      data-testid="supplier-form-web" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label={tip("Email", "E-mail", "Correo")}>
              <Input value={form.company_admin_email}
                      onChange={e => set("company_admin_email", e.target.value)}
                      data-testid="supplier-form-email" />
            </Field>
            <Field label={tip("US Air responsible", "Responsable US Air", "Responsable US Air")}>
              <Input value={form.usair_responsible}
                      onChange={e => set("usair_responsible", e.target.value)}
                      data-testid="supplier-form-responsible" />
            </Field>
          </div>

          {mode === "create" && (
            <div className="border-t border-slate-200 pt-2">
              <div className="text-[10px] font-bold text-slate-500 uppercase mb-2">
                {tip("Primary contact (optional)",
                      "Contact principal (facultatif)",
                      "Contacto principal (opcional)")}
              </div>
              <div className="grid grid-cols-1 gap-2">
                <Field label={tip("Name", "Nom", "Nombre")}>
                  <Input value={form.primary_contact_name}
                          onChange={e => set("primary_contact_name", e.target.value)}
                          data-testid="supplier-form-pc-name" />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label={tip("Email", "E-mail", "Correo")}>
                    <Input value={form.primary_contact_email}
                            onChange={e => set("primary_contact_email", e.target.value)}
                            data-testid="supplier-form-pc-email" />
                  </Field>
                  <Field label={tip("Phone", "Téléphone", "Teléfono")}>
                    <Input value={form.primary_contact_phone}
                            onChange={e => set("primary_contact_phone", e.target.value)}
                            data-testid="supplier-form-pc-phone" />
                  </Field>
                </div>
              </div>
            </div>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer font-bold text-slate-600 py-1">
              {tip("Banking info (optional)", "Informations bancaires (facultatif)",
                    "Información bancaria (opcional)")}
            </summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Field label={tip("Bank", "Banque", "Banco")}>
                <Input value={form.bank_name} onChange={e => set("bank_name", e.target.value)} />
              </Field>
              <Field label={tip("Branch", "Agence", "Sucursal")}>
                <Input value={form.branch} onChange={e => set("branch", e.target.value)} />
              </Field>
              <Field label={tip("Account #", "N° de compte", "N° de cuenta")}>
                <Input value={form.account_number} onChange={e => set("account_number", e.target.value)} />
              </Field>
              <Field label="IBAN">
                <Input value={form.iban} onChange={e => set("iban", e.target.value)} />
              </Field>
              <Field label={tip("Routing #", "Routing", "ABA")}>
                <Input value={form.routing_number} onChange={e => set("routing_number", e.target.value)} />
              </Field>
              <Field label="SWIFT">
                <Input value={form.swift_code} onChange={e => set("swift_code", e.target.value)} />
              </Field>
            </div>
          </details>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}
                   data-testid="supplier-form-cancel">
            {tip("Cancel", "Annuler", "Cancelar")}
          </Button>
          <Button onClick={submit} disabled={busy}
                   className="bg-indigo-600 hover:bg-indigo-700 text-white"
                   data-testid="supplier-form-save">
            <FloppyDisk size={12} className="mr-1" weight="bold" />
            {busy ? tip("Saving…", "Enregistrement…", "Guardando…")
                  : tip("Save", "Enregistrer", "Guardar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ContactFormDialog({ mode, initial = {}, tip, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name:        initial.name || "",
    email:       initial.email || "",
    phone:       initial.phone || "",
    type:        initial.type || "",
    explanation: initial.explanation || "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(tip("Name is required", "Le nom est requis", "Se requiere el nombre"));
      return;
    }
    setBusy(true);
    try {
      await onSaved(form);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="supplier-contact-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === "add"
              ? tip("Add contact", "Ajouter un contact", "Añadir contacto")
              : tip("Edit contact", "Modifier le contact", "Editar contacto")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Field label={tip("Name *", "Nom *", "Nombre *")}>
            <Input value={form.name} onChange={e => set("name", e.target.value)}
                    data-testid="supplier-contact-name" />
          </Field>
          <Field label={tip("Type / Role", "Type / Rôle", "Tipo / Rol")}>
            <Input placeholder={tip("e.g. Purchasing, Sales, Accounting…",
                                      "p. ex. Achats, Ventes, Comptabilité…",
                                      "p. ej. Compras, Ventas, Contabilidad…")}
                    value={form.type} onChange={e => set("type", e.target.value)}
                    data-testid="supplier-contact-type" />
          </Field>
          <Field label={tip("Email", "E-mail", "Correo")}>
            <Input value={form.email} onChange={e => set("email", e.target.value)}
                    data-testid="supplier-contact-email" />
          </Field>
          <Field label={tip("Phone", "Téléphone", "Teléfono")}>
            <Input value={form.phone} onChange={e => set("phone", e.target.value)}
                    data-testid="supplier-contact-phone" />
          </Field>
          <Field label={tip("Notes", "Notes", "Notas")}>
            <Textarea value={form.explanation} onChange={e => set("explanation", e.target.value)}
                       rows={2} data-testid="supplier-contact-notes" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {tip("Cancel", "Annuler", "Cancelar")}
          </Button>
          <Button onClick={submit} disabled={busy}
                   className="bg-indigo-600 hover:bg-indigo-700 text-white"
                   data-testid="supplier-contact-save">
            <FloppyDisk size={12} className="mr-1" weight="bold" />
            {busy ? tip("Saving…", "Enregistrement…", "Guardando…")
                  : tip("Save", "Enregistrer", "Guardar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function NoteFormDialog({ tip, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ note_body: "", note_for: "", cc: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.note_body.trim()) {
      toast.error(tip("Note is empty", "La note est vide", "La nota está vacía"));
      return;
    }
    setBusy(true);
    try { await onSaved(form); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="supplier-note-dialog">
        <DialogHeader>
          <DialogTitle>{tip("Add note", "Ajouter une note", "Añadir nota")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Field label={tip("Note for (optional)", "Note pour (facultatif)", "Nota para (opcional)")}>
            <Input value={form.note_for} onChange={e => set("note_for", e.target.value)}
                    data-testid="supplier-note-for" />
          </Field>
          <Field label={tip("Note body *", "Corps de la note *", "Cuerpo de la nota *")}>
            <Textarea rows={5} value={form.note_body} onChange={e => set("note_body", e.target.value)}
                       data-testid="supplier-note-body" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {tip("Cancel", "Annuler", "Cancelar")}
          </Button>
          <Button onClick={submit} disabled={busy}
                   className="bg-indigo-600 hover:bg-indigo-700 text-white"
                   data-testid="supplier-note-save">
            <FloppyDisk size={12} className="mr-1" weight="bold" />
            {busy ? tip("Saving…", "Enregistrement…", "Guardando…")
                  : tip("Save", "Enregistrer", "Guardar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
